import * as React from 'react'
import { ReactElement, useEffect, useState } from 'react'
import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
    Field,
    Input,
} from '@fluentui/react-components'
import { BankTransactionDto } from '../../../../../../config/finance/invoiceConfig'
import { BankTransactionProps } from '../../BankTransaction'
import LocalDatePicker from '../../../../../DatePicker/LocalDatePicker'
import MoneyInput from '../../../../../Input/MoneyInput'
import { CommitEventType } from '../../../../../Input/FormattableNumberInput'
import { CheckmarkRegular, DismissRegular } from '@fluentui/react-icons'
import useAxios from '../../../../../../hooks/useAxios'
import { useTranslation } from 'react-i18next'

type Props = BankTransactionProps & {
    isNew: boolean
    open: boolean
    setOpen: (open: boolean) => void
}

export default function BankTransactionDialog(props: Props): ReactElement {
    const axios = useAxios()
    const { t: translate } = useTranslation()

    const [values, setValues] = useState<BankTransactionDto>()

    useEffect(() => {
        setValues(props.bankTransaction)
    }, [props.open])

    if (!values) {
        return <></>
    }

    return (
        <Dialog
            open={props.open}
            onOpenChange={(_, data) => {
                if (props.isNew) {
                    props.remove(props.index)
                }
                props.setOpen(data.open)
            }}
        >
            <DialogSurface>
                <DialogBody>
                    <DialogTitle>
                        {translate(
                            props.isNew
                                ? 'invoice.edit.form.banktransactions.edit.title_add'
                                : 'invoice.edit.form.banktransactions.edit.title_edit',
                        )}
                    </DialogTitle>
                    <DialogContent>
                        <Field label={translate('invoice.edit.form.banktransactions.edit.date')}>
                            <LocalDatePicker isoValue={values.date} onSelectDate={date => (values.date = date)} />
                        </Field>
                        <Field label={translate('invoice.edit.form.banktransactions.edit.value')}>
                            <MoneyInput
                                allowNegative
                                value={values.value}
                                currency={props.invoice?.price?.currency || 'EUR'}
                                onCommit={(eventType: CommitEventType, value: string) => {
                                    if (eventType !== CommitEventType.OnLoad) {
                                        values.value = value || '0.00'
                                    }
                                }}
                            />
                        </Field>
                        <Field label={translate('invoice.edit.form.banktransactions.edit.description')}>
                            <Input
                                defaultValue={values.description}
                                placeholder={translate('invoice.edit.form.banktransactions.edit.description')}
                                onChange={(_, { value }) => (values.description = value)}
                            />
                        </Field>
                    </DialogContent>

                    <DialogActions>
                        <DialogTrigger disableButtonEnhancement>
                            <Button appearance="transparent" icon={<DismissRegular />}>
                                {translate('action.cancel')}
                            </Button>
                        </DialogTrigger>
                        <Button
                            appearance="primary"
                            icon={<CheckmarkRegular />}
                            onClick={() => {
                                ;(props.isNew
                                    ? axios.post<string>(`/invoice/${props.invoice.id}/bank-transaction`, values)
                                    : axios.put<string>(
                                          `/invoice/${props.invoice.id}/bank-transaction/${values.id}`,
                                          values,
                                      )
                                ).then(response => {
                                    values.id = response.data

                                    props.setOpen(false)
                                })
                            }}
                        >
                            {translate('action.save')}
                        </Button>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    )
}
